import type { TransferModelMetrics } from "@prequel/react";

import type { TransferSummary } from "@/types/destination-activities";

export enum TransferStatus {
  CANCELLED = "CANCELLED",
  ERROR = "ERROR",
  EXPIRED = "EXPIRED",
  PARTIAL_FAILURE = "PARTIAL_FAILURE",
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  SUCCESS = "SUCCESS",
  KILLED = "KILLED",
  ORPHANED = "ORPHANED",
}

// Coded errors recorded when a transfer ended before a model finished, rather than the model failing.
export enum UnfinishedModelErrorCode {
  TRANSFER_CANCELLED = "PRQL-QZTX",
  TRANSFER_ORPHANED = "PRQL-QVHM",
}

const UNFINISHED_MODEL_STATUS: Record<UnfinishedModelErrorCode, TransferStatus> = {
  [UnfinishedModelErrorCode.TRANSFER_CANCELLED]: TransferStatus.CANCELLED,
  [UnfinishedModelErrorCode.TRANSFER_ORPHANED]: TransferStatus.ORPHANED,
};

// Transfer statuses that mean the transfer was stopped, so its unfinished models didn't fail.
// EXPIRED belongs here too, but its unfinished models are recorded as PRQL-ERMS (unknown error),
// which is indistinguishable from a real failure.
const STOPPED_TRANSFER_STATUSES: TransferStatus[] = [TransferStatus.CANCELLED, TransferStatus.KILLED];

export interface ModelsSummary {
  total: number;
  succeeded: number;
  failed: number;
  pending: number;
}

export const getModelDisplayStatus = (
  transferStatus: TransferStatus,
  metric?: Pick<TransferModelMetrics, "error">,
): TransferStatus => {
  // a model without metrics hasn't reported yet, so it shares the status of the transfer
  if (!metric) {
    return transferStatus;
  }

  if (!metric.error) {
    return TransferStatus.SUCCESS;
  }

  const unfinishedStatus = UNFINISHED_MODEL_STATUS[metric.error.error_code as UnfinishedModelErrorCode];
  if (!unfinishedStatus) {
    return TransferStatus.ERROR;
  }

  // a stopped transfer's status says how it stopped, e.g. KILLED rather than CANCELLED
  return STOPPED_TRANSFER_STATUSES.includes(transferStatus) ? transferStatus : unfinishedStatus;
};

export const summarizeTransferModels = (transfer: TransferSummary): ModelsSummary => {
  const modelMetrics = transfer.model_metrics || [];
  const total = transfer.models.length;
  const status = transfer.status as TransferStatus;

  // an errored transfer that reported no metrics failed every model
  if (modelMetrics.length === 0 && status === TransferStatus.ERROR) {
    return { total, succeeded: 0, failed: total, pending: 0 };
  }

  const errored = modelMetrics.filter((metric) => metric.error).length;
  const succeeded = modelMetrics.length - errored;
  // the summary carries no error codes, so a stopped transfer's errored models count as unfinished
  const failed = STOPPED_TRANSFER_STATUSES.includes(status) ? 0 : errored;
  // models without metrics haven't reported yet
  const pending = Math.max(total - succeeded - failed, 0);

  return { total, succeeded, failed, pending };
};
